/**
 * Portfolio 类型管理 Hook
 * Invest Portfolio 仅 EXTERNAL；不再依赖 userFolders。
 */

import { useCallback, useMemo, useState } from "react";
import { useParams } from "next/navigation";

import log from "logbeacon";
import { useMemberUser } from "./useMemberUser";
import { usePortfolioDefaultLocation } from "./usePortfolioDefaultLocation";
import { usePortfolioRouting } from "./usePortfolioRouting";
import { ALL_PORTFOLIO_OPTION_ID } from "@/src/components/PortfolioContainer/constants";
import { PortfolioType } from "@/src/components/Portfolio/types";
import { usePortfolioConnections } from "@/src/hooks/plaid/usePortfolioConnections";
import { useShellRouter } from "./useShellAwareRouter";

export interface UsePortfolioTypeReturn {
  currentType: PortfolioType;
  switchType: (type: PortfolioType) => void;
  isSwitching: boolean;
  isLoading: boolean;
}

export const usePortfolioType = (): UsePortfolioTypeReturn => {
  const router = useShellRouter();
  const params = useParams();
  const [isSwitching, setIsSwitching] = useState(false);
  const { isImportPage, buildHoldingsPath, buildImportPath } =
    usePortfolioRouting();

  const { defaultType, isInitializing } = usePortfolioDefaultLocation();
  const { plaidConnections, isLoading: isConnectionsLoading } =
    usePortfolioConnections();

  const portfolioId = params?.portfolioId as string | undefined;
  const { data: memberPlanInfo, isLoading: isMemberLoading } = useMemberUser();

  const isLoading = useMemo(() => {
    if (isImportPage) {
      return false;
    }
    if (isInitializing) {
      return true;
    }
    if (!portfolioId) {
      return true;
    }
    if (isConnectionsLoading) {
      return true;
    }
    return false;
  }, [isConnectionsLoading, isImportPage, isInitializing, portfolioId]);

  const currentType = useMemo(() => {
    if (isImportPage) {
      return PortfolioType.EXTERNAL;
    }
    if (isLoading) {
      return defaultType;
    }
    return PortfolioType.EXTERNAL;
  }, [defaultType, isImportPage, isLoading]);

  const switchType = useCallback(
    (type: PortfolioType) => {
      if (type === currentType) {
        return;
      }

      setIsSwitching(true);
      log.debug("[PortfolioType] Switching portfolio type:", {
        from: currentType,
        to: type,
        portfolioId,
      });

      try {
        if (type === PortfolioType.WATCHLIST) {
          log.warn("[PortfolioType] Watchlist is not supported on Invest");
          return;
        }

        if (plaidConnections.length > 0) {
          const targetId = ALL_PORTFOLIO_OPTION_ID;
          router.replace(buildHoldingsPath(targetId));
          return;
        }

        if (isMemberLoading) {
          return;
        }

        const isMember = !memberPlanInfo?.isFree;
        const importPath = buildImportPath();
        router.replace(
          isMember ? `${importPath}?showImportModal=true` : importPath,
        );
      } catch (error) {
        log.error("[PortfolioType] Failed to switch type:", error);
      } finally {
        setTimeout(() => {
          setIsSwitching(false);
        }, 100);
      }
    },
    [
      buildHoldingsPath,
      buildImportPath,
      currentType,
      isMemberLoading,
      memberPlanInfo,
      plaidConnections.length,
      portfolioId,
      router,
    ],
  );

  return {
    currentType,
    switchType,
    isSwitching,
    isLoading,
  };
};
